// src/components/TrafficSignMarker.js
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from 'react-dom';
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { signTypes } from "../data/fakeSignals";
import { getElement } from "../data/signalElements";
import { Button, Col, Row, Spin, Image } from "antd";
import { MdDelete } from 'react-icons/md';
import { EnvironmentOutlined } from "@ant-design/icons";
import TrafficSignTooltip from './TrafficSignTooltip';
import "../styles/signal-fix-components.css";
import { GrEdit } from "react-icons/gr";
import { LuScan } from "react-icons/lu";
import { useTranslation } from 'react-i18next';
import { FaCube } from "react-icons/fa";

const TrafficSignMarker = ({ sign, onMarkerClick, isSelected, isMuted = false, onView3D, onDelete, animationState }) => {
  const { t } = useTranslation();
  const [streetName, setStreetName] = useState("");
  const [loadingStreet, setLoadingStreet] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipPortal, setTooltipPortal] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const markerRef = useRef(); // reference to the marker element

  const normalizedType = sign.type
    .replace(/-/g, "-")
    .toLowerCase()
    .replace(/\//g, "_");
  const signType = signTypes[normalizedType] || {};

  // v2 point/pole elements (tombino, buca, lanterna...) carry their own SVG.
  const v2Element = getElement(sign.type);
  const v2IconSvg = v2Element?.iconSvg ? v2Element.iconSvg(30) : null;

  // Determine background color based on animation state
  let animationColor = null;
  if (animationState === 'green') animationColor = '#A1E430';
  else if (animationState === 'yellow') animationColor = '#FFD700';
  else if (animationState === 'red') animationColor = '#ff4d4f';

  // Create portal container for tooltip
  useEffect(() => {
    const portalDiv = document.createElement('div');
    portalDiv.id = `traffic-sign-tooltip-portal-${sign.id}`;
    document.body.appendChild(portalDiv);
    setTooltipPortal(portalDiv);

    return () => {
      if (document.body.contains(portalDiv)) {
        document.body.removeChild(portalDiv);
      }
    };
  }, [sign.id]);

  useEffect(() => {
    setImageLoading(true);
  }, [sign.image_url]);

  const handlePopupClose = (event) => {
    event?.stopPropagation();
    if (markerRef.current) {
      markerRef.current.closePopup();
    }
    setPreviewVisible(false);
    setShowPopup(false);
  };

  const handleMarkerClick = (event) => {
    setShowPopup(true);
  };

  const handleEditClick = () => {
    setShowTooltip(false);
    setShowPopup(true);
    if (onMarkerClick) {
      onMarkerClick(sign);
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTooltip && !event.target.closest('.traffic-sign-tooltip') && !event.target.closest('.traffic-sign-marker')) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTooltip]);
  // Check if sign is Segnaletica (horizontal marking)
  const isSegnaletica = sign.Segnaletica === true;

  // "Segnaletica verticale" = road signs on a post. They get a pole whose base
  // sits on the georeferenced point and the sign plate on top. v2 elements
  // (tombino, lanterna, ...), horizontal signage and green-wave-animated
  // markers keep their own flat representation.
  const isVerticalSign = !v2IconSvg && !isSegnaletica && !animationColor;

  const imgSize = isVerticalSign ? 30 : 20;
  const iconInner = v2IconSvg
    ? v2IconSvg
    : signType.iconUrl
      ? `<img src="${signType.iconUrl}" style="width:${imgSize}px;height:${imgSize}px;object-fit:contain;display:block;" />`
      : `${signType.icon || "🚦"}`;

  let markerIcon;
  if (isVerticalSign) {
    const HEAD = 30; // sign plate size
    const BAR = 20;  // pole height
    markerIcon = L.divIcon({
      className: "traffic-sign-marker",
      html: `
        <div class="traffic-sign-pole ${isSelected ? "traffic-sign-pole-active" : ""} ${isMuted ? "traffic-sign-muted" : ""}">
          <div class="traffic-sign-pole-head">${iconInner}</div>
          <div class="traffic-sign-pole-bar"></div>
        </div>
      `,
      iconSize: [HEAD, HEAD + BAR],
      iconAnchor: [HEAD / 2, HEAD + BAR], // georeferenced point = base of the pole
      popupAnchor: [0, -(HEAD + BAR)],
    });
  } else {
    // v2 elements render their own SVG on a transparent, slightly larger canvas.
    const iconPx = v2IconSvg ? 34 : animationColor ? 32 : 24;
    markerIcon = L.divIcon({
      className: "traffic-sign-marker",
      html: `
        <div class="traffic-sign-marker-icon ${
          isSelected ? "traffic-sign-marker-active" : ""
        } ${isMuted ? "traffic-sign-muted" : ""} ${isSegnaletica ? "segnaletica-marker" : ""} ${v2IconSvg ? "v2-element-marker" : ""} ${animationState ? 'animating' : ''}" style="
          background-color: ${animationColor || (isSegnaletica && !v2IconSvg ? "white" : "transparent")};
          border: ${animationColor ? '2px solid white' : (isSegnaletica && !v2IconSvg ? "2px solid #8C74FF" : "none")};
          box-shadow: ${animationColor ? '0 0 10px ' + animationColor : (isSegnaletica && !v2IconSvg ? "3px 3px 3px #8C74FF" : "none")};
          border-radius: ${animationColor ? '50%' : (isSegnaletica && !v2IconSvg ? "8px" : "0")};
          padding: ${isSegnaletica && !v2IconSvg ? "10px" : "0"};
          color: ${animationColor ? 'white' : 'inherit'};">
          ${iconInner}
        </div>
      `,
      iconSize: [iconPx, iconPx],
      iconAnchor: [iconPx / 2, iconPx / 2],
      popupAnchor: [20, 0],
    });
  }

  useEffect(() => {
    const fetchStreet = async () => {
      setLoadingStreet(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${sign.lat}&lon=${sign.lng}`
        );
        const data = await response.json();
        const roadName =
          data.address?.road ||
          data.address?.pedestrian ||
          data.display_name ||
          "Unknown street";
        setStreetName(roadName);
      } catch (error) {
        console.error("Error during reverse geocoding:", error);
        setStreetName("Unknown street");
      } finally {
        setLoadingStreet(false);
      }
    };
    if (isSelected) {
      fetchStreet();

      const timeout = setTimeout(() => {
        if (markerRef.current) {
          markerRef.current.closePopup();
        }
      }, 4000);

      return () => clearTimeout(timeout);
    }
  }, [isSelected, sign.lat, sign.lng]);

  // Ensure valid coordinates before rendering
  if (
    !sign?.lat ||
    !sign?.lng ||
    isNaN(Number(sign.lat)) ||
    isNaN(Number(sign.lng))
  ) {
    console.warn("TrafficSignMarker: Invalid coordinates", {
      lat: sign?.lat,
      lng: sign?.lng,
    });
    return null;
  }

  const position = [Number(sign.lat), Number(sign.lng)];

  return (
    <>
      <Marker
        position={position}
        icon={markerIcon}
        ref={markerRef}
        eventHandlers={{
          click: (e) => {
            e.originalEvent.stopPropagation();
            handleMarkerClick(e);
          }
        }}
      >
        <Popup autoPan={true} className="ply-3d-popup-override" style={{ padding: 0 }}>
          <TrafficSignTooltip
            isInsidePopup={true}
            sign={sign}
            onView3D={(signData) => {
              if (onView3D) onView3D(signData);
            }}
            onEdit={() => handleEditClick()}
            onDelete={(signData) => {
              handlePopupClose();
              if (onDelete) onDelete(signData);
            }}
            onClose={handlePopupClose}
            visible={true}
          />
        </Popup>
      </Marker>


    </>
  );
};

export default TrafficSignMarker;
